import { moment } from '@eventbrite/date';
import { snakeToCamel } from '@eventbrite/string-utils';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import { Dispatch } from 'redux';
import { OrderResponse } from '../api/models/order';
import {
    InteractiveQuestion,
    Question,
    QuestionsCollection,
    QuestionsResponses,
    QuestionType,
} from '../api/models/survey';
import { getSurveyResponsesByAttendeeIdFromOrderResponse } from '../api/transformations/order';
import {
    getInteractiveQuestionsByAttendeeIdFromOrderResponse,
    getQuestionsCollectionFromOrderResponse,
} from '../api/transformations/survey';
import { ORGANIZER_QUESTIONS_FORM_NAME } from '../constants';
import { CHECKOUT_FORM_NAME } from '../constants/checkoutForm';
import {
    BUYER_EMAIL_FIELD,
    BUYER_FIRST_NAME_FIELD,
    BUYER_LAST_NAME_FIELD,
    CANNED_QUESTION_EMAIL_ADDRESS,
    CANNED_QUESTION_FIRST_NAME,
    CANNED_QUESTION_LAST_NAME,
    getFieldName,
} from '../containers/checkout/constants';
import { FormAnswer, PrefillSurveyFieldsInfo } from '../models/survey';
import {
    getFormName,
    getFormValuesWithoutSyncValidationErroredFields,
} from '../selectors/forms';
import { getOrderAttendeeById } from '../selectors/order/typedOrderSelectors';
import {
    getPrefillFirstAttendeeInfo,
    getPrefillSurveyInfo,
    getQuestionsCollection,
    getSurveyResponsesByAttendeeId,
    shouldPrefillFirstAttendeeBasicInfoQuestions,
} from '../selectors/survey';
import { getNamespaceValue } from '../selectors/utils/selector';
import { CheckoutState } from '../state';
import {
    areMultipleResponsesSelected,
    changeSurveyField,
    getChoiceIdByText,
    getMultipleChoicesSelected,
    transformAddressFromApi,
} from '../utils/surveys';
import { prefillAttendeeBasicInfoFields } from './prefillAttendeeBasicInfoFields';
import { submitOptionalOrganizerQuestionsForm } from './submitOptionalOrganizerQuestionsForm';

export enum SurveyActionType {
    initSurveys = 'initSurveys',
}

export interface InitSurveysAction {
    type: SurveyActionType.initSurveys;
    payload: {
        questionsCollection: QuestionsCollection;
        interactiveQuestionsByAttendeeId: Record<
            string,
            Array<InteractiveQuestion>
        >;
        surveyResponsesByAttendeeId: Record<string, QuestionsResponses>;
    };
}

export const getInitSurveysAction = (payload: {
    questionsCollection: QuestionsCollection;
    interactiveQuestionsByAttendeeId: Record<
        string,
        Array<InteractiveQuestion>
    >;
    surveyResponsesByAttendeeId: Record<string, QuestionsResponses>;
}): InitSurveysAction => ({
    type: SurveyActionType.initSurveys,
    payload,
});

export const getInitSurveysActionFromOrderResponse = (
    orderResponse: OrderResponse,
): InitSurveysAction =>
    getInitSurveysAction({
        questionsCollection:
            getQuestionsCollectionFromOrderResponse(orderResponse) ?? {},
        interactiveQuestionsByAttendeeId:
            getInteractiveQuestionsByAttendeeIdFromOrderResponse(
                orderResponse,
            ) ?? {},
        surveyResponsesByAttendeeId:
            getSurveyResponsesByAttendeeIdFromOrderResponse(orderResponse),
    });

export type SurveyAction = InitSurveysAction;

export const prefillSurveyFields =
    ({
        formName = CHECKOUT_FORM_NAME,
        emailToPrefill = '',
        firstNameToPrefill = '',
        lastNameToPrefill = '',
        attendeeWithSurvey,
        isTransferredOrder,
        shouldManageUserSavedSurveyData,
        shouldPrefillFirstAttendeeBasicInfoQuestions,
        surveyResponseNameValues = {},
    }: PrefillSurveyFieldsInfo) =>
    (dispatch: Dispatch<any>) => {
        if (emailToPrefill) {
            dispatch(
                changeSurveyField(BUYER_EMAIL_FIELD, emailToPrefill, formName),
            );
        }

        if (!shouldManageUserSavedSurveyData) {
            return;
        }

        if (firstNameToPrefill) {
            dispatch(
                changeSurveyField(
                    BUYER_FIRST_NAME_FIELD,
                    firstNameToPrefill,
                    formName,
                ),
            );
        }

        if (lastNameToPrefill) {
            dispatch(
                changeSurveyField(
                    BUYER_LAST_NAME_FIELD,
                    lastNameToPrefill,
                    formName,
                ),
            );
        }

        if (isTransferredOrder) {
            Object.entries(surveyResponseNameValues).forEach(
                ([surveyFieldName, surveyFieldValue]) =>
                    dispatch(
                        changeSurveyField(
                            surveyFieldName,
                            surveyFieldValue,
                            formName,
                        ),
                    ),
            );
        } else if (
            shouldPrefillFirstAttendeeBasicInfoQuestions &&
            attendeeWithSurvey
        ) {
            dispatch(
                prefillAttendeeBasicInfoFields({
                    attendeeId: attendeeWithSurvey.id,
                    emailToPrefill,
                    firstNameToPrefill,
                    lastNameToPrefill,
                    formName,
                }),
            );
        }
    };

export const prefillFirstAttendeeSurvey =
    ({
        formName,
        emailToPrefill,
        firstNameToPrefill,
        lastNameToPrefill,
        attendeeWithSurvey,
    }: PrefillSurveyFieldsInfo) =>
    (dispatch: Dispatch<CheckoutState>, getState: () => CheckoutState) => {
        if (
            shouldPrefillFirstAttendeeBasicInfoQuestions(getState()) &&
            attendeeWithSurvey
        ) {
            dispatch(
                prefillAttendeeBasicInfoFields({
                    attendeeId: attendeeWithSurvey.id,
                    emailToPrefill,
                    firstNameToPrefill,
                    lastNameToPrefill,
                    formName,
                }),
            );
        }
    };

export const getSurveyInfoAndPrefillFieldsAction =
    () => (dispatch: Dispatch<any>, getState: () => CheckoutState) => {
        const state = getState();
        const prefillSurveyFieldsInfo = getPrefillSurveyInfo(state);

        dispatch(prefillSurveyFields(prefillSurveyFieldsInfo));
    };

export const getSurveyInfoAndPrefillFirstAttendeeAction =
    (formName: string) =>
    (dispatch: Dispatch<CheckoutState>, getState: () => CheckoutState) => {
        const state = getState();
        const prefillFirstAttendeeInfo = {
            ...getPrefillFirstAttendeeInfo(state),
            formName,
        };

        dispatch(prefillFirstAttendeeSurvey(prefillFirstAttendeeInfo));
    };

export const handleOrganizerQuestionsPageSkip =
    () =>
    async (
        dispatch: Dispatch<CheckoutState>,
        getState: () => CheckoutState,
    ) => {
        const state = getState();

        // When pressing the Organizer Questions page > Skip button, we will bypass the
        // validation process by removing the errored fields from the form data to be submitted
        const formDataWithoutValidationErroredFields =
            getFormValuesWithoutSyncValidationErroredFields(
                state,
                ORGANIZER_QUESTIONS_FORM_NAME,
            );

        if (isEmpty(formDataWithoutValidationErroredFields)) {
            return;
        }

        await dispatch(
            submitOptionalOrganizerQuestionsForm(
                formDataWithoutValidationErroredFields,
                true,
            ),
        );
    };

interface PrefillMethodProps {
    dispatch: Dispatch<any>;
    question: Question;
    questionName: string;
    responses: Record<string, FormAnswer>;
    attendeeId: string;
}

const prefillDateQuestion = ({
    dispatch,
    questionName,
    responses,
    attendeeId,
}: PrefillMethodProps) => {
    const date = moment(responses[questionName], 'MM/DD/YYYY');
    const namespace = `${attendeeId}.${questionName}`;

    dispatch(
        changeSurveyField(
            getFieldName(namespace, 'day'),
            date.date().toString(),
            ORGANIZER_QUESTIONS_FORM_NAME,
        ),
    );

    dispatch(
        changeSurveyField(
            getFieldName(namespace, 'month'),
            (date.month() + 1).toString(),
            ORGANIZER_QUESTIONS_FORM_NAME,
        ),
    );

    dispatch(
        changeSurveyField(
            getFieldName(namespace, 'year'),
            date.year().toString(),
            ORGANIZER_QUESTIONS_FORM_NAME,
        ),
    );
};

const prefillAddressQuestion = ({
    dispatch,
    questionName,
    responses,
    attendeeId,
}: PrefillMethodProps) => {
    const addressFieldsResponses = transformAddressFromApi(
        getNamespaceValue(responses, questionName),
    );

    for (const response of Object.keys(addressFieldsResponses)) {
        dispatch(
            changeSurveyField(
                getFieldName(`${attendeeId}.${questionName}`, response),
                addressFieldsResponses[response],
                ORGANIZER_QUESTIONS_FORM_NAME,
            ),
        );
    }
};

const prefillCheckboxQuestion = ({
    dispatch,
    question,
    questionName,
    responses,
    attendeeId,
}: PrefillMethodProps) => {
    const selectedChoices = !areMultipleResponsesSelected(
        responses[questionName] as string,
    )
        ? [getChoiceIdByText(question, questionName, responses) as string]
        : getMultipleChoicesSelected(
              question,
              responses[questionName] as string,
          );

    dispatch(
        changeSurveyField(
            getFieldName(attendeeId, questionName),
            selectedChoices as unknown as string,
            ORGANIZER_QUESTIONS_FORM_NAME,
        ),
    );
};

const prefillQuestionWithChoices = ({
    dispatch,
    question,
    questionName,
    responses,
    attendeeId,
}: PrefillMethodProps) => {
    const choice = getChoiceIdByText(question, questionName, responses);

    dispatch(
        changeSurveyField(
            getFieldName(attendeeId, questionName),
            choice as string,
            ORGANIZER_QUESTIONS_FORM_NAME,
        ),
    );
};

const getPrefillQuestionHandler: Partial<
    Record<QuestionType, (props: PrefillMethodProps) => void>
> = {
    [QuestionType.address]: prefillAddressQuestion,
    [QuestionType.checkbox]: prefillCheckboxQuestion,
    [QuestionType.date]: prefillDateQuestion,
    [QuestionType.dropdown]: prefillQuestionWithChoices,
    [QuestionType.radio]: prefillQuestionWithChoices,
};

const prefillSingleAttendeeQuestions = (
    state: CheckoutState,
    dispatch: Dispatch<any>,
    responses: Record<string, FormAnswer>,
    attendeeId = 'buyer',
    survey?: Array<InteractiveQuestion> | undefined,
) => {
    const isNotBuyerAttendeeId = attendeeId !== 'buyer';

    if (isEmpty(survey) && isNotBuyerAttendeeId) {
        return;
    }

    const questionsCollection = getQuestionsCollection(state);
    const surveyQuestionsIds = survey?.map((question) => question.questionId);
    const questionsFoundInSurvey = isNotBuyerAttendeeId
        ? pick(responses, surveyQuestionsIds || [])
        : responses;

    for (const questionName of Object.keys(questionsFoundInSurvey)) {
        const question = questionsCollection[snakeToCamel(questionName)];

        const prefillQuestionMethod = getPrefillQuestionHandler[question.type];

        if (prefillQuestionMethod) {
            prefillQuestionMethod({
                dispatch,
                question,
                questionName,
                responses,
                attendeeId,
            });
        } else {
            dispatch(
                changeSurveyField(
                    getFieldName(attendeeId, questionName),
                    responses[questionName] as string,
                    ORGANIZER_QUESTIONS_FORM_NAME,
                ),
            );
        }
    }
};

const prefillAttendeesQuestions = (
    dispatch: Dispatch<any>,
    state: CheckoutState,
) => {
    const surveyResponses = getSurveyResponsesByAttendeeId(state);
    if (!surveyResponses) {
        return;
    }
    for (const attendeeId of Object.keys(surveyResponses)) {
        const orderAttendee = getOrderAttendeeById(state, attendeeId);
        prefillSingleAttendeeQuestions(
            state,
            dispatch,
            surveyResponses[attendeeId],
            attendeeId,
            orderAttendee?.survey,
        );
    }
};

export const prefillOrganizerQuestionsForm =
    () => (dispatch: Dispatch<any>, getState: () => CheckoutState) =>
        prefillAttendeesQuestions(dispatch, getState());

export const updateFirstAttendeeBasicInfo =
    (copyValuesFromFirstAttendee: boolean) =>
    (dispatch: Dispatch<CheckoutState>, getState: () => CheckoutState) => {
        const fields = getPrefillFirstAttendeeInfo(getState());
        const attendeeId = fields.attendeeWithSurvey?.id || '';
        const formName = getFormName(getState());

        if (copyValuesFromFirstAttendee) {
            const { emailToPrefill, firstNameToPrefill, lastNameToPrefill } =
                fields;
            dispatch(
                prefillAttendeeBasicInfoFields({
                    formName,
                    emailToPrefill,
                    firstNameToPrefill,
                    lastNameToPrefill,
                    attendeeId,
                }),
            );
        } else {
            [
                CANNED_QUESTION_EMAIL_ADDRESS,
                CANNED_QUESTION_FIRST_NAME,
                CANNED_QUESTION_LAST_NAME,
            ].map((questionName) => {
                dispatch(
                    changeSurveyField(
                        getFieldName(attendeeId, questionName),
                        '',
                        formName,
                    ),
                );
            });
        }
    };
